import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { produce } from "immer";

export interface ApiCallRecord {
  id: string;
  timestamp: Date;
  endpoint: string;
  method: string;
  request: any;
  response?: any;
  error?: string;
  status: "pending" | "success" | "error";
  duration?: number;
}

interface ApiStoreState {
  history: ApiCallRecord[];
  maxHistorySize: number;
  addRecord: (record: Omit<ApiCallRecord, "id" | "timestamp">) => string;
  updateRecord: (
    id: string,
    updates: Partial<Omit<ApiCallRecord, "id" | "timestamp">>,
  ) => void;
  clearHistory: () => void;
  getStats: () => {
    total: number;
    success: number;
    error: number;
    pending: number;
  };
}

export const useApiStore = create(
  devtools<ApiStoreState>((set, get) => {
    const update = (updater: (state: ApiStoreState) => void) =>
      set(produce(updater));

    return {
      history: [],
      maxHistorySize: 500,

      addRecord: (record) => {
        const id = `api-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newRecord: ApiCallRecord = {
          ...record,
          id,
          timestamp: new Date(),
        };

        update((state) => {
          state.history.unshift(newRecord);

          // Keep only last maxHistorySize records
          if (state.history.length > state.maxHistorySize) {
            state.history = state.history.slice(0, state.maxHistorySize);
          }
        });

        return id;
      },

      updateRecord: (id, updates) => {
        update((state) => {
          const record = state.history.find((r) => r.id === id);
          if (record) {
            Object.assign(record, updates);
          }
        });
      },

      clearHistory: () => {
        update((state) => {
          state.history = [];
        });
      },

      getStats: () => {
        const history = get().history;
        return {
          total: history.length,
          success: history.filter((r) => r.status === "success").length,
          error: history.filter((r) => r.status === "error").length,
          pending: history.filter((r) => r.status === "pending").length,
        };
      },
    };
  }),
);
